import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Building2, FileText, User, X } from 'lucide-react';

const deduplicateAddress = (addr) => {
  if (!addr || typeof addr !== 'string' || addr.length < 20) return addr || '';
  const firstWord = addr.split(/[,\s]+/)[0].toLowerCase();
  if (firstWord.length < 3) return addr;
  let lastCommaIdx = addr.lastIndexOf(',');
  while (lastCommaIdx > 0) {
    const afterComma = addr.substring(lastCommaIdx + 1).trim();
    if (afterComma.toLowerCase().startsWith(firstWord)) {
      return addr.substring(0, lastCommaIdx).trim();
    }
    lastCommaIdx = addr.lastIndexOf(',', lastCommaIdx - 1);
  }
  return addr;
};

export function CriminalComparison({ check, verificationData, setVerificationData, handleFieldVerification }) {
    const rawSD = check?.subcheck_data || {};
    const candidateData = {
      ...rawSD,
      complete_address: deduplicateAddress(rawSD.complete_address),
      father_name: check?.father_name || rawSD.father_name || '',
      dob: check?.dob || rawSD.dob || rawSD.date_of_birth || '',
      candidate_name: rawSD.candidate_name || rawSD.name || check?.employee_name || '',
    };
    
    // Verification status options with color codes
    const verificationStatusOptions = [
      { value: 'verified', label: 'Verified', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50 border-green-300' },
      { value: 'discrepancy', label: 'Discrepancy', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 border-red-300' },
      { value: 'partially_verified', label: 'Partially Verified', color: 'bg-sky-400', textColor: 'text-sky-700', bgLight: 'bg-sky-50 border-sky-300' },
      { value: 'not_verified', label: 'Not Verified', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50 border-amber-300' },
    ];
    
    // Get status color classes
    const getStatusClasses = (status) => {
      const found = verificationStatusOptions.find(o => o.value === status);
      return found || { color: 'bg-gray-300', textColor: 'text-gray-600', bgLight: 'bg-white border-gray-200' };
    };

    const caseStatusOptions = [
      { value: 'no_records', label: 'No Records Found', color: 'text-green-600' },
      { value: 'case_pending', label: 'Case Pending', color: 'text-amber-600' },
      { value: 'case_disposed', label: 'Case Disposed', color: 'text-blue-600' },
      { value: 'acquitted', label: 'Acquitted', color: 'text-green-600' },
      { value: 'convicted', label: 'Convicted', color: 'text-red-600' },
      { value: 'withdrawn', label: 'Case Withdrawn', color: 'text-slate-600' },
      { value: 'compromised', label: 'Compromised/Settled', color: 'text-blue-600' },
    ];

    const caseTypeOptions = [
      { value: 'criminal', label: 'Criminal' },
      { value: 'civil', label: 'Civil' },
      { value: 'matrimonial', label: 'Matrimonial' },
      { value: 'cheque_bounce', label: 'Cheque Bounce (138 NI Act)' },
      { value: 'property', label: 'Property Dispute' },
      { value: 'consumer', label: 'Consumer Case' },
      { value: 'labor', label: 'Labor Dispute' },
      { value: 'tax', label: 'Tax Related' },
      { value: 'motor_accident', label: 'Motor Accident Claim' },
      { value: 'cyber_crime', label: 'Cyber Crime' },
      { value: 'dowry', label: 'Dowry Related' },
      { value: 'domestic_violence', label: 'Domestic Violence' },
      { value: 'land_revenue', label: 'Land Revenue' },
      { value: 'company_law', label: 'Company Law' },
      { value: 'intellectual_property', label: 'Intellectual Property' },
      { value: 'environmental', label: 'Environmental' },
      { value: 'arbitration', label: 'Arbitration' },
      { value: 'writ_petition', label: 'Writ Petition' },
      { value: 'other', label: 'Other' },
    ];

    // Expanded court types
    const courtTypes = [
      { value: 'all_courts', label: 'All Courts' },
      { value: 'supreme_court', label: 'Supreme Court of India' },
      { value: 'high_court', label: 'High Court' },
      { value: 'district_court', label: 'District Court' },
      { value: 'sessions_court', label: 'Sessions Court' },
      { value: 'additional_sessions', label: 'Additional Sessions Court' },
      { value: 'chief_judicial_magistrate', label: 'Chief Judicial Magistrate Court' },
      { value: 'judicial_magistrate_first', label: 'Judicial Magistrate First Class (JMFC)' },
      { value: 'judicial_magistrate_second', label: 'Judicial Magistrate Second Class' },
      { value: 'metropolitan_magistrate', label: 'Metropolitan Magistrate Court' },
      { value: 'executive_magistrate', label: 'Executive Magistrate Court' },
      { value: 'family_court', label: 'Family Court' },
      { value: 'consumer_court_district', label: 'District Consumer Disputes Redressal Forum' },
      { value: 'consumer_court_state', label: 'State Consumer Disputes Redressal Commission' },
      { value: 'consumer_court_national', label: 'National Consumer Disputes Redressal Commission' },
      { value: 'labor_court', label: 'Labor Court' },
      { value: 'industrial_tribunal', label: 'Industrial Tribunal' },
      { value: 'central_administrative_tribunal', label: 'Central Administrative Tribunal (CAT)' },
      { value: 'state_administrative_tribunal', label: 'State Administrative Tribunal (SAT)' },
      { value: 'income_tax_tribunal', label: 'Income Tax Appellate Tribunal (ITAT)' },
      { value: 'customs_tribunal', label: 'Customs, Excise and Service Tax Appellate Tribunal (CESTAT)' },
      { value: 'debt_recovery_tribunal', label: 'Debt Recovery Tribunal (DRT)' },
      { value: 'national_green_tribunal', label: 'National Green Tribunal (NGT)' },
      { value: 'nclt', label: 'National Company Law Tribunal (NCLT)' },
      { value: 'nclat', label: 'National Company Law Appellate Tribunal (NCLAT)' },
      { value: 'rera_tribunal', label: 'Real Estate Regulatory Authority (RERA)' },
      { value: 'motor_accident_tribunal', label: 'Motor Accident Claims Tribunal (MACT)' },
      { value: 'lok_adalat', label: 'Lok Adalat' },
      { value: 'gram_nyayalaya', label: 'Gram Nyayalaya' },
      { value: 'fast_track_court', label: 'Fast Track Court' },
      { value: 'special_court', label: 'Special Court' },
      { value: 'cbi_court', label: 'CBI Special Court' },
      { value: 'nia_court', label: 'NIA Special Court' },
      { value: 'pocso_court', label: 'POCSO Court' },
      { value: 'sc_st_court', label: 'SC/ST (Prevention of Atrocities) Court' },
      { value: 'juvenile_court', label: 'Juvenile Justice Board' },
      { value: 'cyber_tribunal', label: 'Cyber Appellate Tribunal' },
      { value: 'electricity_tribunal', label: 'Appellate Tribunal for Electricity' },
      { value: 'armed_forces_tribunal', label: 'Armed Forces Tribunal' },
      { value: 'railway_claims_tribunal', label: 'Railway Claims Tribunal' },
      { value: 'telecom_disputes_tribunal', label: 'Telecom Disputes Settlement & Appellate Tribunal' },
      { value: 'securities_appellate_tribunal', label: 'Securities Appellate Tribunal (SAT)' },
      { value: 'competition_appellate_tribunal', label: 'Competition Appellate Tribunal' },
      { value: 'other', label: 'Other' },
    ];
    
    // Clear form function for Criminal
    const clearCriminalForm = () => {
      setVerificationData(prev => ({
        ...prev,
        verified_details: {
          ...prev.verified_details,
          candidate_name: '',
          name_status: '',
          father_name: '',
          dob: '',
          address: '',
          district: '',
          state: '',
          pan_number: '',
          aadhar_number: '',
          case_status: '',
          case_type: '',
          court_type: '',
          court_name: '',
          case_number: '',
          filing_date: '',
          next_hearing: '',
          disposal_date: '',
          judgment_summary: '',
          advocate_name: '',
          advocate_contact: '',
          advocate_bar_council: '',
          verification_method: '',
          verification_date: '',
          additional_remarks: '',
          // New fields
          period_verified_from: '',
          period_verified_to: '',
          police_station_name: '',
          police_district: '',
          police_contact_name: '',
          police_contact_phone: '',
          fir_number: '',
          fir_date: '',
          section_applied: '',
        }
      }));
      toast.info('Form cleared');
    };
    
    return (
      <div className="space-y-4">
        {/* Header with Clear Button */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-3 rounded-lg shadow-lg">
          <h4 className="font-semibold text-base flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Criminal / Court Record Verification - {check?.subcheck_type || 'Criminal Check'}
          </h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearCriminalForm}
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Form
          </Button>
        </div>
        
        {/* Status Color Legend */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border">
          <span className="text-xs font-medium text-slate-600">Status Legend:</span>
          <div className="flex items-center gap-3">
            {verificationStatusOptions.map(opt => (
              <div key={opt.value} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${opt.color}`}></div>
                <span className={`text-xs ${opt.textColor}`}>{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Main Comparison Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Candidate Provided (Left) */}
          <Card className="border-2 border-blue-200 shadow-md bg-gradient-to-b from-blue-50 to-white">
            <CardHeader className="pb-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                Candidate Details for Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm pt-4">
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Candidate Name</span>
                <span className="font-semibold text-slate-800">{candidateData.candidate_name || candidateData.name || check?.employee_name || '-'}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Father's Name</span>
                  <span className="font-semibold text-slate-800">{candidateData.father_name || candidateData.fathers_name || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Date of Birth</span>
                  <span className="font-semibold text-slate-800">{candidateData.dob || candidateData.date_of_birth || '-'}</span>
                </div>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Address (for jurisdiction)</span>
                <span className="font-semibold text-slate-800">{candidateData.address || candidateData.complete_address || '-'}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">District</span>
                  <span className="font-semibold text-slate-800">{candidateData.district || candidateData.city || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">State</span>
                  <span className="font-semibold text-slate-800">{candidateData.state || '-'}</span>
                </div>
              </div>
              
              {candidateData.pan_number && (
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">PAN Number</span>
                  <span className="font-semibold text-slate-800">{candidateData.pan_number}</span>
                </div>
              )}
              
              {candidateData.aadhar_number && (
                <div className="p-2 bg-blue-50 rounded">
                  <span className="text-xs text-blue-600 font-medium">Aadhar Number</span>
                  <p className="font-medium">{candidateData.aadhar_number}</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Verified Details (Right) */}
          <Card className="border-red-200 bg-gradient-to-b from-red-50 to-white">
            <CardHeader className="pb-2 bg-red-100/50">
              <CardTitle className="text-sm text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Court Verification Result
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-3">
              {/* Overall Status */}
              <div className="p-3 bg-white rounded-lg border-2 border-red-200">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-bold text-red-800">Case Status</Label>
                </div>
                <Select 
                  value={verificationData.verified_details.case_status || ''} 
                  onValueChange={(v) => handleFieldVerification('case_status', true, v)}
                >
                  <SelectTrigger className="h-9 text-sm font-medium"><SelectValue placeholder="Select case status" /></SelectTrigger>
                  <SelectContent>
                    {caseStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Name & Father Verification */}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-white rounded-lg border">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-medium">Name Verified</Label>
                    <Select 
                      value={verificationData.verified_details.name_status || ''} 
                      onValueChange={(v) => handleFieldVerification('name_status', true, v)}
                    >
                      <SelectTrigger className="h-5 w-20 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input 
                    value={verificationData.verified_details.verified_name || ''} 
                    onChange={(e) => handleFieldVerification('verified_name', true, e.target.value)}
                    className="h-7 text-sm"
                    placeholder="Name as per records"
                  />
                </div>
                <div className="p-2 bg-white rounded-lg border">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-medium">Father's Name</Label>
                    <Select 
                      value={verificationData.verified_details.father_status || ''} 
                      onValueChange={(v) => handleFieldVerification('father_status', true, v)}
                    >
                      <SelectTrigger className="h-5 w-20 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input 
                    value={verificationData.verified_details.father_name || ''} 
                    onChange={(e) => handleFieldVerification('father_name', true, e.target.value)}
                    className="h-7 text-sm"
                    placeholder="Father's name"
                  />
                </div>
              </div>
              
              {/* Court & District */}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-white rounded-lg border">
                  <Label className="text-xs font-medium mb-1 block">Court Type</Label>
                  <Select 
                    value={verificationData.verified_details.court_type || ''} 
                    onValueChange={(v) => handleFieldVerification('court_type', true, v)}
                  >
                    <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select court" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {courtTypes.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="p-2 bg-white rounded-lg border">
                  <Label className="text-xs font-medium mb-1 block">District/Jurisdiction</Label>
                  <Input 
                    value={verificationData.verified_details.jurisdiction || ''} 
                    onChange={(e) => handleFieldVerification('jurisdiction', true, e.target.value)}
                    className="h-7 text-sm"
                    placeholder="Court jurisdiction"
                  />
                </div>
              </div>
              
              {/* Court Name */}
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Court Name</Label>
                <Input 
                  value={verificationData.verified_details.court_name || ''} 
                  onChange={(e) => handleFieldVerification('court_name', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Full court name"
                />
              </div>
              
              {/* Period Verified For */}
              <div className="p-3 bg-indigo-50 rounded-lg border-2 border-indigo-200">
                <Label className="text-xs font-semibold text-indigo-700 mb-2 block">Period Verified For</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">From Date</Label>
                    <Input 
                      type="date"
                      value={verificationData.verified_details.period_verified_from || ''} 
                      onChange={(e) => handleFieldVerification('period_verified_from', true, e.target.value)}
                      className="h-7 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">To Date</Label>
                    <Input 
                      type="date"
                      value={verificationData.verified_details.period_verified_to || ''} 
                      onChange={(e) => handleFieldVerification('period_verified_to', true, e.target.value)}
                      className="h-7 text-sm"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Police Station Details Section */}
        <Card className="border-2 border-blue-300 shadow-md">
          <CardHeader className="pb-2 bg-gradient-to-r from-blue-700 to-blue-600 text-white rounded-t-lg">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Police Station Details (for FIR/Criminal Records)
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 pt-4 bg-gradient-to-b from-blue-50 to-white">
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border-2 border-blue-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">Police Station Name</Label>
                <Input 
                  value={verificationData.verified_details.police_station_name || ''} 
                  onChange={(e) => handleFieldVerification('police_station_name', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="e.g., Koramangala Police Station"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border-2 border-blue-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">District/Jurisdiction</Label>
                <Input 
                  value={verificationData.verified_details.police_district || ''} 
                  onChange={(e) => handleFieldVerification('police_district', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="District name"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border-2 border-blue-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">Contact Person Name</Label>
                <Input 
                  value={verificationData.verified_details.police_contact_name || ''} 
                  onChange={(e) => handleFieldVerification('police_contact_name', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="SHO/Officer name"
                />
              </div>
            </div>
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border-2 border-blue-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">Contact Phone Number</Label>
                <Input 
                  value={verificationData.verified_details.police_contact_phone || ''} 
                  onChange={(e) => handleFieldVerification('police_contact_phone', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="Phone number"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border-2 border-blue-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">FIR Number (if any)</Label>
                <Input 
                  value={verificationData.verified_details.fir_number || ''} 
                  onChange={(e) => handleFieldVerification('fir_number', true, e.target.value)}
                  className="h-8 text-sm"
                  placeholder="FIR/CR number"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-white rounded-lg border-2 border-blue-100 shadow-sm">
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">FIR Date</Label>
                  <Input 
                    type="date"
                    value={verificationData.verified_details.fir_date || ''} 
                    onChange={(e) => handleFieldVerification('fir_date', true, e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="p-2 bg-white rounded-lg border-2 border-blue-100 shadow-sm">
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Section Applied</Label>
                  <Input 
                    value={verificationData.verified_details.section_applied || ''} 
                    onChange={(e) => handleFieldVerification('section_applied', true, e.target.value)}
                    className="h-8 text-sm"
                    placeholder="e.g., IPC 420"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Case Details Section (if case found) */}
        <Card className="border-orange-200">
          <CardHeader className="pb-2 bg-gradient-to-r from-orange-50 to-red-50">
            <CardTitle className="text-sm text-orange-800 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Case Details (if any record found)
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 pt-3">
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Case Type</Label>
                <Select 
                  value={verificationData.verified_details.case_type || ''} 
                  onValueChange={(v) => handleFieldVerification('case_type', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {caseTypeOptions.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Case Number</Label>
                <Input 
                  value={verificationData.verified_details.case_number || ''} 
                  onChange={(e) => handleFieldVerification('case_number', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Case/FIR number"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Year of Filing</Label>
                <Input 
                  value={verificationData.verified_details.case_year || ''} 
                  onChange={(e) => handleFieldVerification('case_year', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="e.g., 2020"
                />
              </div>
            </div>
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Section/IPC</Label>
                <Input 
                  value={verificationData.verified_details.sections || ''} 
                  onChange={(e) => handleFieldVerification('sections', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="e.g., IPC 420, 406"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Role in Case</Label>
                <Select 
                  value={verificationData.verified_details.role_in_case || ''} 
                  onValueChange={(v) => handleFieldVerification('role_in_case', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accused">Accused</SelectItem>
                    <SelectItem value="complainant">Complainant</SelectItem>
                    <SelectItem value="witness">Witness</SelectItem>
                    <SelectItem value="defendant">Defendant</SelectItem>
                    <SelectItem value="petitioner">Petitioner</SelectItem>
                    <SelectItem value="respondent">Respondent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Next Hearing Date</Label>
                <Input 
                  type="date"
                  value={verificationData.verified_details.next_hearing || ''} 
                  onChange={(e) => handleFieldVerification('next_hearing', true, e.target.value)}
                  className="h-7 text-sm"
                />
              </div>
            </div>
            <div className="col-span-2 p-2 bg-white rounded-lg border">
              <Label className="text-xs font-medium mb-1 block">Case Summary/Details</Label>
              <Textarea 
                value={verificationData.verified_details.case_details || ''} 
                onChange={(e) => handleFieldVerification('case_details', true, e.target.value)}
                className="h-16 text-sm resize-none"
                placeholder="Brief description of the case (if any found)..."
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Advocate Verification Section */}
        <Card className="border-purple-200">
          <CardHeader className="pb-2 bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardTitle className="text-sm text-purple-800 flex items-center gap-2">
              <User className="w-4 h-4" />
              Advocate Verification Details
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 pt-3">
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Advocate Name</Label>
                <Input 
                  value={verificationData.verified_details.advocate_name || ''} 
                  onChange={(e) => handleFieldVerification('advocate_name', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Name of advocate"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Bar Council Number</Label>
                <Input 
                  value={verificationData.verified_details.bar_council_number || ''} 
                  onChange={(e) => handleFieldVerification('bar_council_number', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Enrollment number"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Advocate Contact</Label>
                <Input 
                  value={verificationData.verified_details.advocate_contact || ''} 
                  onChange={(e) => handleFieldVerification('advocate_contact', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Verification Method</Label>
                <Select 
                  value={verificationData.verified_details.criminal_verification_method || ''} 
                  onValueChange={(v) => handleFieldVerification('criminal_verification_method', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="advocate_visit">Advocate Visit to Court</SelectItem>
                    <SelectItem value="ecourt_portal">eCourt Portal Search</SelectItem>
                    <SelectItem value="physical_visit">Physical Court Visit</SelectItem>
                    <SelectItem value="police_verification">Police Verification</SelectItem>
                    <SelectItem value="online_database">Online Database Search</SelectItem>
                    <SelectItem value="third_party">Third Party Verification</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Search Period</Label>
                <Input 
                  value={verificationData.verified_details.search_period || ''} 
                  onChange={(e) => handleFieldVerification('search_period', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="e.g., Last 7 years"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Verification Date</Label>
                <Input 
                  type="date"
                  value={verificationData.verified_details.criminal_verification_date || new Date().toISOString().split('T')[0]} 
                  onChange={(e) => handleFieldVerification('criminal_verification_date', true, e.target.value)}
                  className="h-7 text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
}
